import { formsEngineEndpoints } from '@/lib/api/endpoints';
import { publicPost, toPublicApiUrl } from '@/lib/api/publicClient';
import type { PublicIntakeUploadInput, PublicIntakeUploadTicket } from '@/types/formsEngine';

/** Ask the server for a presigned PUT for one FILE_UPLOAD file on a permanent intake link. */
export function requestIntakeUpload(
  key: string,
  input: PublicIntakeUploadInput,
): Promise<PublicIntakeUploadTicket> {
  return publicPost<PublicIntakeUploadTicket, PublicIntakeUploadInput>(
    formsEngineEndpoints.publicIntakeUpload(key),
    input,
  );
}

export class UploadAbortedError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'UploadAbortedError';
  }
}

/**
 * PUT the raw file to a presigned URL. XMLHttpRequest rather than fetch,
 * because fetch cannot report upload progress. `onProgress` receives 0–100.
 */
export function putFileWithProgress({
  ticket,
  file,
  onProgress,
  signal,
}: {
  ticket: Pick<PublicIntakeUploadTicket, 'uploadUrl' | 'method' | 'headers'>;
  file: Blob;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(ticket.method ?? 'PUT', toPublicApiUrl(ticket.uploadUrl));
    for (const [name, value] of Object.entries(ticket.headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    };
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    const settle = () => signal?.removeEventListener('abort', onAbort);
    xhr.onload = () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      settle();
      reject(new Error('Upload failed. Check your connection and try again.'));
    };
    xhr.onabort = () => {
      settle();
      reject(new UploadAbortedError());
    };
    xhr.send(file);
  });
}
